import { useState, useRef, useEffect, useCallback } from 'react'
import { api, aiAnalyticsApi, type ApiQaAgentResult, type ApiWebsiteScoreResult, type ApiWebsiteDoctorResult } from '../utils/api'
import { useEditorStore } from '../store/editor.store'
import { WebsiteScorePanel } from './WebsiteScorePanel'
import { QaReportPanel } from './QaReportPanel'
import { WebsiteDoctorModal } from './WebsiteDoctorModal'

type AssistantAction =
  | { type: 'add_section'; description: string; blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }
  | { type: 'remove_block'; blockId: string }
  | { type: 'update_block'; blockId: string; data: Record<string, unknown> }
  | { type: 'change_theme'; accentColor: string; bgColor?: string }

interface SiteIssue { code: string; title: string; description?: string; suggestion?: string }
interface SiteAnalysis {
  overallScore: number
  scores: { structure: number; content: number; ux: number; seo: number }
  verdict: string
  critical: SiteIssue[]
  important: SiteIssue[]
  good: SiteIssue[]
}

interface Message {
  role: 'user' | 'assistant'
  content: string
  copyText?: string
  themeColors?: Record<string, string>
  actionDone?: string
  analysis?: SiteAnalysis
  blockApply?: { sectionId: string; blockId: string; data: Record<string, unknown> }
  qaResult?: ApiQaAgentResult
  scoreResult?: ApiWebsiteScoreResult
  doctorResult?: ApiWebsiteDoctorResult
}

function scoreColor(n: number) {
  if (n >= 85) return '#22c55e'
  if (n >= 70) return '#3b82f6'
  if (n >= 50) return '#f59e0b'
  return '#ef4444'
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ width: 70, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${value}%`, height: '100%', background: scoreColor(value), borderRadius: 2, transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ width: 24, textAlign: 'right', color: scoreColor(value), fontWeight: 600 }}>{value}</span>
    </div>
  )
}

function SiteAnalysisView({ a }: { a: SiteAnalysis }) {
  const col = scoreColor(a.overallScore)
  return (
    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      {/* Score + verdict */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
          border: `3px solid ${col}`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexDirection: 'column',
          background: `${col}18`,
        }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: col, lineHeight: 1 }}>{a.overallScore}</span>
          <span style={{ fontSize: 8, color: 'var(--text-muted)', lineHeight: 1 }}>/100</span>
        </div>
        <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.4, fontSize: 12 }}>{a.verdict}</p>
      </div>

      {/* Sub-scores */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '8px 10px', background: 'var(--bg-1)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <ScoreBar label="Структура" value={a.scores.structure} />
        <ScoreBar label="Контент"   value={a.scores.content} />
        <ScoreBar label="UX"        value={a.scores.ux} />
        <ScoreBar label="SEO"       value={a.scores.seo} />
      </div>

      {/* Critical */}
      {a.critical.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            🔴 Критично ({a.critical.length})
          </div>
          {a.critical.map(issue => (
            <div key={issue.code} style={{ padding: '6px 8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, marginBottom: 4 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{issue.title}</div>
              {issue.description && <div style={{ color: 'var(--text-muted)', lineHeight: 1.3, marginBottom: 2 }}>{issue.description}</div>}
              {issue.suggestion && <div style={{ color: '#93c5fd', fontStyle: 'italic' }}>→ {issue.suggestion}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Important */}
      {a.important.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            🟡 Важно ({a.important.length})
          </div>
          {a.important.map(issue => (
            <div key={issue.code} style={{ padding: '6px 8px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 6, marginBottom: 4 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{issue.title}</div>
              {issue.description && <div style={{ color: 'var(--text-muted)', lineHeight: 1.3, marginBottom: 2 }}>{issue.description}</div>}
              {issue.suggestion && <div style={{ color: '#93c5fd', fontStyle: 'italic' }}>→ {issue.suggestion}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Good */}
      {a.good.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            ✅ Хорошо ({a.good.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {a.good.map(issue => (
              <span key={issue.code} style={{ padding: '3px 8px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, color: '#86efac', fontSize: 11 }}>
                {issue.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const pill = (active: boolean): React.CSSProperties => ({
  padding: '5px 11px', borderRadius: '20px',
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  background: active ? 'var(--accent-light)' : 'var(--bg-2)',
  color: active ? 'var(--accent)' : 'var(--text-secondary)',
  cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit',
  fontWeight: active ? 600 : 400, display: 'flex', alignItems: 'center',
  gap: '4px', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
})

function getPageContext() {
  const { page, settings } = useEditorStore.getState()
  const blocks = page.sections.flatMap(s => s.blocks.map(b => ({ id: b.id, type: b.type })))
  return {
    siteName: settings.name || '',
    pageTitle: page.title || '',
    blockTypes: blocks.map(b => b.type),
    blocks,
  }
}

function getFullPageData() {
  const { page } = useEditorStore.getState()
  return {
    title: page.title || '',
    sections: page.sections.map(s => ({
      name: s.name || '',
      blockTypes: s.blocks.map(b => b.type),
      blockData: s.blocks.map(b => {
        const filtered: Record<string, unknown> = {}
        let count = 0
        for (const [k, v] of Object.entries(b.data)) {
          if (count >= 4) break
          if (typeof v === 'string' && v.length < 200 && !v.startsWith('data:') && !/^https?:\/\//.test(v)) {
            filtered[k] = v
            count++
          }
        }
        return { type: b.type, ...filtered }
      }),
    })),
  }
}

interface PendingTransaction {
  replyText: string
  actions: AssistantAction[]
}

function actionLabel(action: AssistantAction): string {
  switch (action.type) {
    case 'add_section':  return `➕ ${action.description || 'Добавить секцию'} (${action.blocks.length} блоков)`
    case 'remove_block': return `🗑 Удалить блок ${action.blockId}`
    case 'update_block': return `✏️ Обновить блок ${action.blockId}`
    case 'change_theme': return `🎨 Изменить тему → ${action.accentColor}`
    default: return 'Действие AI'
  }
}

function isDestructive(action: AssistantAction): boolean {
  return action.type === 'remove_block' || action.type === 'change_theme'
}

function applyAction(action: AssistantAction): string {
  const store = useEditorStore.getState()
  switch (action.type) {
    case 'add_section': {
      if (!action.blocks?.length) return ''
      store.addSection()
      const added = useEditorStore.getState().page.sections.at(-1)
      if (!added) return ''
      for (const block of action.blocks) store.addBlock(added.id, block.type, block.data)
      return `✅ ${action.description || 'Секция добавлена'}`
    }
    case 'remove_block': {
      const page = useEditorStore.getState().page
      const sec = page.sections.find(s => s.blocks.some(b => b.id === action.blockId))
      if (sec) store.removeBlock(sec.id, action.blockId)
      return '✅ Блок удалён'
    }
    case 'update_block': {
      const page = useEditorStore.getState().page
      const sec = page.sections.find(s => s.blocks.some(b => b.id === action.blockId))
      const blk = sec?.blocks.find(b => b.id === action.blockId)
      if (sec && blk) store.updateBlock(sec.id, action.blockId, { ...blk.data, ...action.data })
      return '✅ Блок обновлён'
    }
    case 'change_theme': {
      // Merge into the existing theme (keeps AI design tokens, fonts and designSystem).
      // The renderer reads primaryColor, not accentColor.
      const current = (() => { try { return JSON.parse(useEditorStore.getState().settings.themeConfig || '{}') as Record<string, unknown> } catch { return {} } })()
      store.updateSettings({ themeConfig: JSON.stringify({
        ...current,
        ...(action.accentColor ? { primaryColor: action.accentColor } : {}),
        ...(action.bgColor ? { bgColor: action.bgColor } : {}),
      }) })
      return '✅ Тема изменена'
    }
    default:
      return ''
  }
}


export function AiChatPanel({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [showSectionInput, setShowSectionInput] = useState(false)
  const [sectionPrompt, setSectionPrompt] = useState('')
  const [pendingTx, setPendingTx] = useState<PendingTransaction | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const sectionInputRef = useRef<HTMLInputElement>(null)

  // Smart greeting on mount
  useEffect(() => {
    const { page, settings } = useEditorStore.getState()
    const blockTypes = page.sections.flatMap(s => s.blocks.map(b => b.type))
    const hasCTA         = blockTypes.some(t => /cta|banner/.test(t))
    const hasTestimonial = blockTypes.some(t => /testimonial|review/.test(t))
    const hasStats       = blockTypes.some(t => /stat|counter|kpi/.test(t))
    const hasForm        = blockTypes.some(t => /form|contact|lead/.test(t))

    const tips: string[] = []
    if (blockTypes.length === 0) {
      tips.push('Страница пустая — нажми «➕ Секция», чтобы добавить первый блок')
    } else {
      if (!hasCTA)         tips.push('• Нет CTA — добавь призыв к действию')
      if (!hasTestimonial) tips.push('• Нет отзывов — социальное доказательство повышает доверие')
      if (!hasStats)       tips.push('• Нет статистики — конкретные цифры убеждают')
      if (!hasForm)        tips.push('• Нет формы — добавь способ связи')
    }

    const siteName = settings.name || 'Без названия'
    const greeting = tips.length > 0
      ? `Анализирую «${page.title}» (${blockTypes.length} блоков)...\n\n${tips.slice(0, 3).join('\n')}\n\nЧем помочь?`
      : `«${page.title}» — ${blockTypes.length} блоков, выглядит сбалансированно!\n\nЧем могу помочь с сайтом «${siteName}»?`
    setMessages([{ role: 'assistant', content: greeting }])
  }, [])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => { if (showSectionInput) sectionInputRef.current?.focus() }, [showSectionInput])

  // ── Suggest next blocks ───────────────────────────────────────────────────────
  // (called from quick-pill: не тратит quota, просто загружается при открытии панели)
  const [suggestions, setSuggestions] = useState<Array<{ type: string; name: string; reason: string }>>([])
  useEffect(() => {
    const { page } = useEditorStore.getState()
    const existingTypes = page.sections.flatMap(s => s.blocks.map(b => b.type)).slice(0, 15)
    if (existingTypes.length > 0) {
      api.post<Array<{ type: string; name: string; reason: string }>>('/ai/suggest-blocks', { existingTypes })
        .then(r => setSuggestions(Array.isArray(r) ? r : []))
        .catch(() => null)
    }
  }, [])

  const addSuggestedBlock = async (blockType: string) => {
    const store = useEditorStore.getState()
    const ctx = getPageContext()
    setLoading(true)
    try {
      const result = await api.post<{ blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>(
        '/ai/generate-section',
        { prompt: `Добавь блок "${blockType}"`, pageContext: `${ctx.siteName} — ${ctx.pageTitle}` },
      )
      store.addSection()
      const added = useEditorStore.getState().page.sections.at(-1)
      if (added && result.blocks?.length) {
        for (const block of result.blocks) store.addBlock(added.id, block.type, block.data)
      }
      setSuggestions(prev => prev.filter(s => s.type !== blockType))
    } catch { /* silent */ } finally { setLoading(false) }
  }

  // ── Основной AI-чат через /ai/assistant ──────────────────────────────────────
  const send = useCallback(async (text: string) => {
    if (!text.trim() || loading) return
    const userMsg: Message = { role: 'user', content: text.trim() }
    const updated = [...messages, userMsg]
    setMessages(updated)
    setInput('')
    setLoading(true)

    try {
      const ctx = getPageContext()
      const result = await api.post<{ reply: string; actions: AssistantAction[] }>('/ai/assistant', {
        messages: updated.map(m => ({ role: m.role, content: m.content })),
        siteName: ctx.siteName,
        pageTitle: ctx.pageTitle,
        blockTypes: ctx.blockTypes.slice(0, 20),
        blocks: ctx.blocks.slice(0, 20),
      })

      const actions = result.actions ?? []
      const approvalLevel = useEditorStore.getState().settings.aiApprovalLevel ?? 'ask_destructive'

      const needsApproval = approvalLevel === 'always_ask'
        ? actions.length > 0
        : approvalLevel === 'ask_destructive'
          ? actions.some(isDestructive)
          : false

      if (needsApproval && actions.length > 0) {
        // Store pending transaction — user must approve before applying
        setPendingTx({ replyText: result.reply ?? '...', actions })
        setMessages(prev => [...prev, { role: 'assistant', content: result.reply ?? '...', actionDone: undefined }])
      } else {
        let actionDone = ''
        for (const action of actions) {
          const done = applyAction(action)
          if (done) actionDone = done
        }
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: result.reply ?? '...',
          actionDone: actionDone || undefined,
        }])
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Ошибка соединения с AI. Попробуйте ещё раз.' }])
    } finally {
      setLoading(false)
    }
  }, [messages, loading])

  // ── Генерация секции через /ai/generate-section ──────────────────────────────
  const handleAddSection = async () => {
    const prompt = sectionPrompt.trim()
    if (!prompt || loading) return
    setMessages(prev => [...prev, { role: 'user', content: `➕ Добавь секцию: ${prompt}` }])
    setSectionPrompt('')
    setShowSectionInput(false)
    setLoading(true)
    try {
      const ctx = getPageContext()
      const { settings } = useEditorStore.getState()
      const pageCtx = `Сайт: «${ctx.siteName}». Страница: «${ctx.pageTitle}». Блоки: ${ctx.blockTypes.slice(0, 8).join(', ') || 'нет'}`
      const result = await api.post<{ blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>(
        '/ai/generate-section', { prompt, pageContext: pageCtx, style: settings.designStyle || undefined },
      )
      const store = useEditorStore.getState()
      store.addSection()
      const added = useEditorStore.getState().page.sections.at(-1)
      if (added && result.blocks?.length) {
        for (const block of result.blocks) store.addBlock(added.id, block.type, block.data)
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Секция добавлена в конец страницы (${result.blocks.length} блоков). Прокрутите холст вниз.`,
          actionDone: `✅ ${result.blocks.length} блоков добавлено`,
        }])
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Секция создана, но осталась пустой. Опишите подробнее.' }])
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось сгенерировать секцию. Попробуйте позже.' }])
    } finally {
      setLoading(false)
    }
  }

  // ── Написать текст для выбранного блока ──────────────────────────────────────
  const handleWriteCopy = async () => {
    const { page, selectedBlockId, settings } = useEditorStore.getState()
    const section = page.sections.find(s => s.blocks.some(b => b.id === selectedBlockId))
    const block = section?.blocks.find(b => b.id === selectedBlockId) ?? null
    if (!block || !section) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Выберите блок на холсте, затем нажмите «✍ Текст».' }])
      return
    }
    setMessages(prev => [...prev, { role: 'user', content: `✍ Напиши текст для блока «${block.type}»` }])
    setLoading(true)
    try {
      const result = await api.post<Record<string, string>>('/ai/generate-copy', {
        blockType: block.type, context: settings.name || 'сайт',
        style: settings.designStyle || undefined,
      })
      const entries = Object.entries(result).filter(([, v]) => v && typeof v === 'string')
      const formatted = entries.map(([k, v]) => `**${k}:** ${v}`).join('\n')
      const copyText  = entries.map(([, v]) => v).join('\n\n')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Текст для «${block.type}»:\n\n${formatted}`,
        copyText,
        blockApply: { sectionId: section.id, blockId: block.id, data: result as Record<string, unknown> },
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось сгенерировать текст. Попробуйте позже.' }])
    } finally {
      setLoading(false)
    }
  }

  // ── Полировка всего сайта (AI content pass) ──────────────────────────────────
  const handlePolish = async () => {
    const { page, settings } = useEditorStore.getState()

    const skipKeys = ['src','url','href','link','image','img','bg','background','color','icon','video','poster','overlay','font','size','width','height','padding','margin','type','id']

    const blocksToPolish = page.sections.flatMap(s =>
      s.blocks.map(b => {
        const textFields: Record<string, string> = {}
        for (const [k, v] of Object.entries(b.data)) {
          if (typeof v !== 'string') continue
          if (v.length < 4 || v.length > 350) continue
          if (v.startsWith('data:') || /^https?:\/\//.test(v) || v.startsWith('#') || v.includes('<') || v.includes('rgb')) continue
          if (skipKeys.some(p => k.toLowerCase().includes(p))) continue
          textFields[k] = v
        }
        return Object.keys(textFields).length > 0 ? { id: b.id, type: b.type, textFields } : null
      }).filter(Boolean),
    ).slice(0, 20) as Array<{ id: string; type: string; textFields: Record<string, string> }>

    if (blocksToPolish.length === 0) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Нет блоков с текстом. Сначала сгенерируйте сайт через AI.' }])
      return
    }

    setMessages(prev => [...prev, { role: 'user', content: `✨ Полировка текстов (${blocksToPolish.length} блоков)...` }])
    setLoading(true)
    try {
      const context = `Сайт: «${settings.name || 'Без названия'}». Стиль: ${settings.designStyle || 'noir'}.`
      const improvements = await api.post<Record<string, Record<string, string>>>('/ai/polish-site', { blocks: blocksToPolish, context })
      const store = useEditorStore.getState()
      const freshPage = store.page
      let improvedCount = 0
      for (const [blockId, fields] of Object.entries(improvements)) {
        if (!fields || Object.keys(fields).length === 0) continue
        const sec = freshPage.sections.find(s => s.blocks.some(b => b.id === blockId))
        const blk = sec?.blocks.find(b => b.id === blockId)
        if (sec && blk) {
          store.updateBlock(sec.id, blockId, { ...blk.data, ...fields })
          improvedCount++
        }
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: improvedCount > 0
          ? `✨ Полировка завершена! Улучшено ${improvedCount} блоков — заголовки стали конкретнее, CTA сильнее.`
          : 'AI проверил контент — тексты уже выглядят профессионально!',
        actionDone: improvedCount > 0 ? `✅ ${improvedCount} блоков улучшено` : undefined,
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось улучшить тексты. Попробуйте позже.' }])
    } finally {
      setLoading(false)
    }
  }

  // ── Подобрать цветовую тему ───────────────────────────────────────────────────
  const handleTheme = async () => {
    const { settings } = useEditorStore.getState()
    setMessages(prev => [...prev, { role: 'user', content: `🎨 Подбери тему для «${settings.name || 'сайта'}»` }])
    setLoading(true)
    try {
      const result = await api.post<{
        primaryColor?: string; accent?: string; bg?: string; bgColor?: string; textColor?: string; text?: string
        name?: string; description?: string
      }>('/ai/generate-theme', { prompt: settings.name || 'современный бизнес' })

      const primary = result.primaryColor ?? result.accent ?? ''
      const bg      = result.bgColor ?? result.bg ?? ''
      const text    = result.textColor ?? result.text ?? ''
      const colors: Record<string, string> = {}
      if (primary) colors['Акцент']  = primary
      if (bg)      colors['Фон']     = bg
      if (text)    colors['Текст']   = text

      const desc = result.description ?? result.name ?? 'Цветовая схема готова'
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${desc}\n\nПрименить: Тема сайта (🎨) в шапке редактора.`,
        themeColors: colors,
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось подобрать тему. Попробуйте позже.' }])
    } finally {
      setLoading(false)
    }
  }

  // ── Анализ страницы (структурированный) ─────────────────────────────────────
  const handleAnalyze = async () => {
    setMessages(prev => [...prev, { role: 'user', content: '🔍 Анализирую страницу...' }])
    setLoading(true)
    try {
      const pageData = getFullPageData()
      const result = await api.post<SiteAnalysis>('/ai/analyze-site', { pageData })
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        analysis: result,
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось провести анализ. Попробуйте ещё раз.' }])
    } finally {
      setLoading(false)
    }
  }

  // ── AI Website Score (9 метрик) ──────────────────────────────────────────────
  const handleWebsiteScore = async () => {
    const { page, settings } = useEditorStore.getState()
    setMessages(prev => [...prev, { role: 'user', content: '📊 Запускаю AI Website Score...' }])
    setLoading(true)
    try {
      const pages = [{ title: page.title, slug: page.slug, sections: page.sections.flatMap(s => s.blocks.map(b => ({ type: b.type, hasCta: /cta|banner/i.test(b.type), hasForm: /form|contact/i.test(b.type) }))) }]
      const result = await aiAnalyticsApi.websiteScore({
        siteName: settings.name || 'Без названия',
        pagesCount: 1,
        pages,
        hasCms: false,
        hasAnalytics: !!(settings.gaId || settings.ymId),
        hasCustomDomain: !!settings.customDomainVerified,
        themeConfig: settings.themeConfig || '',
      })
      setMessages(prev => [...prev, { role: 'assistant', content: '', scoreResult: result }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось запустить AI Score. Попробуйте позже.' }])
    } finally {
      setLoading(false) }
  }

  // ── AI QA Agent ───────────────────────────────────────────────────────────────
  const handleQaAgent = async () => {
    const { page, settings } = useEditorStore.getState()
    setMessages(prev => [...prev, { role: 'user', content: '🧪 Запускаю QA тест...' }])
    setLoading(true)
    try {
      const pages = [{
        title: page.title, slug: page.slug,
        blocks: page.sections.flatMap(s => s.blocks.map(b => ({
          type: b.type,
          hasCta: /cta|banner|button/i.test(b.type),
          hasForm: /form|contact|lead/i.test(b.type),
        }))),
      }]
      const result = await aiAnalyticsApi.qaAgent({ siteName: settings.name || 'Без названия', pages })
      setMessages(prev => [...prev, { role: 'assistant', content: '', qaResult: result }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось запустить QA тест. Попробуйте позже.' }])
    } finally {
      setLoading(false) }
  }

  // ── §59: AI Project Plan ─────────────────────────────────────────────────────
  const handleProjectPlan = async () => {
    const { page, settings } = useEditorStore.getState()
    const existingBlocks = page.sections.flatMap(s => s.blocks.map(b => b.type)).slice(0, 20)
    const prompt = `Создай план улучшения сайта «${settings.name || 'Без названия'}» с ${existingBlocks.length} блоками`
    setMessages(prev => [...prev, { role: 'user', content: '📋 Формирую план...' }])
    setLoading(true)
    try {
      const result = await aiAnalyticsApi.projectPlan(prompt, 1, existingBlocks)
      const lines = result.steps.map(s => `${s.step}. ${s.action} — ${s.detail}`).join('\n')
      const warnings = result.warnings.length > 0 ? `\n\n⚠️ ${result.warnings.join('. ')}` : ''
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**${result.title}**\n\n${result.summary}\n\n${lines}${warnings}\n\n⏱ ${result.estimatedTime}`,
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось сформировать план. Попробуйте позже.' }])
    } finally {
      setLoading(false) }
  }

  // ── §36: AI Natural Language full-site editing ───────────────────────────────
  const [nlEditMode, setNlEditMode] = useState(false)
  const [nlInstruction, setNlInstruction] = useState('')
  const handleNlEdit = async () => {
    const instruction = nlInstruction.trim()
    if (!instruction || loading) return
    const { page, settings } = useEditorStore.getState()
    setMessages(prev => [...prev, { role: 'user', content: `✏️ ${instruction}` }])
    setNlInstruction('')
    setNlEditMode(false)
    setLoading(true)
    try {
      const skipKeys = ['src','url','href','link','image','img','bg','background','color','icon','video','poster']
      const pages = [{
        pageId: page.id,
        sections: page.sections.map(sec => ({
          anchorId: sec.id,
          blocks: sec.blocks.map(b => {
            const textFields: Record<string, string> = {}
            for (const [k, v] of Object.entries(b.data)) {
              if (typeof v !== 'string' || v.length < 4 || v.length > 300) continue
              if (v.startsWith('data:') || /^https?:\/\//.test(v) || v.startsWith('#')) continue
              if (skipKeys.some(p => k.toLowerCase().includes(p))) continue
              textFields[k] = v
            }
            return { id: b.id, type: b.type, textFields }
          }).filter(b => Object.keys(b.textFields).length > 0),
        })).filter(sec => sec.blocks.length > 0),
      }]
      const result = await aiAnalyticsApi.naturalLanguageEdit({ instruction, siteName: settings.name, themeConfig: settings.themeConfig, pages })
      const store = useEditorStore.getState()
      let changed = 0
      for (const change of result.blockChanges) {
        const sec = store.page.sections.find(s => s.id === change.anchorId)
        const blk = sec?.blocks.find(b => b.id === change.blockId)
        if (sec && blk) {
          store.updateBlock(sec.id, blk.id, { ...blk.data, ...change.changes })
          changed++
        }
      }
      if (result.themeChanges && Object.keys(result.themeChanges).length > 0) {
        const current = JSON.parse(settings.themeConfig || '{}')
        store.updateSettings({ themeConfig: JSON.stringify({ ...current, ...result.themeChanges }) })
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${result.plan}\n\n${result.summary}`,
        actionDone: changed > 0 ? `✅ Изменено ${changed} блоков` : undefined,
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось применить редактирование. Попробуйте позже.' }])
    } finally {
      setLoading(false) }
  }

  // ── Website Doctor ────────────────────────────────────────────────────────────
  const [doctorModal, setDoctorModal] = useState<ApiWebsiteDoctorResult | null>(null)
  const handleWebsiteDoctor = async () => {
    const { page, settings } = useEditorStore.getState()
    setMessages(prev => [...prev, { role: 'user', content: '🩺 Запускаю Website Doctor...' }])
    setLoading(true)
    try {
      const pages = [{ title: page.title, slug: page.slug, blockTypes: page.sections.flatMap(s => s.blocks.map(b => b.type)) }]
      const blockTypes = pages[0].blockTypes
      const result = await aiAnalyticsApi.websiteDoctor({
        siteName: settings.name || 'Без названия',
        pagesCount: 1, pages,
        hasAnalytics: !!(settings.gaId || settings.ymId),
        hasCms: false, hasEcommerce: false,
        hasCustomDomain: !!settings.customDomainVerified,
        hasForms: blockTypes.some(t => /form|contact|lead/i.test(t)),
      })
      setMessages(prev => [...prev, { role: 'assistant', content: `🩺 Диагностика завершена! Health Score: ${result.healthScore} (${result.grade}). Нажмите для подробного отчёта.`, doctorResult: result }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Не удалось запустить диагностику. Попробуйте позже.' }])
    } finally {
      setLoading(false) }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
    <div style={{
      position: 'fixed', bottom: '72px', right: '20px', width: '370px', height: '580px',
      background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
      zIndex: 9999, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px',
        background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: '#fff', flexShrink: 0,
      }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>✨</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700 }}>AI Ассистент BST</div>
          <div style={{ fontSize: '10px', opacity: 0.65 }}>BST AI · {useEditorStore.getState().settings.name || 'Новый сайт'}</div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
      </div>

      {/* Quick action pills */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '5px', flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none' }}>
        <button style={pill(showSectionInput)} onClick={() => setShowSectionInput(v => !v)} disabled={loading}>➕ Секция</button>
        <button style={pill(false)} onClick={handleWriteCopy} disabled={loading}>✍ Текст</button>
        <button style={pill(false)} onClick={handleTheme} disabled={loading}>🎨 Тема</button>
        <button style={pill(false)} onClick={handleAnalyze} disabled={loading}>🔍 Анализ</button>
        <button style={pill(false)} onClick={handlePolish} disabled={loading}>✨ Полировка</button>
        <button style={pill(false)} onClick={handleWebsiteScore} disabled={loading}>📊 Score</button>
        <button style={pill(false)} onClick={handleQaAgent} disabled={loading}>🧪 QA</button>
        <button style={pill(false)} onClick={handleWebsiteDoctor} disabled={loading}>🩺 Доктор</button>
        <button style={pill(nlEditMode)} onClick={() => setNlEditMode(v => !v)} disabled={loading}>✏️ Редактировать</button>
        <button style={pill(false)} onClick={handleProjectPlan} disabled={loading}>📋 План</button>
      </div>

      {/* Natural Language Edit inline input */}
      {nlEditMode && (
        <div style={{ padding: '9px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg-2)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ✏️ Редактирование всего сайта
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              value={nlInstruction}
              onChange={e => setNlInstruction(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void handleNlEdit(); if (e.key === 'Escape') setNlEditMode(false) }}
              placeholder="Make the site more premium..."
              style={{ flex: 1, padding: '7px 10px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
            />
            <button
              onClick={() => void handleNlEdit()}
              disabled={!nlInstruction.trim() || loading}
              style={{ padding: '7px 13px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, opacity: !nlInstruction.trim() ? 0.5 : 1, flexShrink: 0 }}
            >↵</button>
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            Примеры: "Make it more premium" · "Increase conversion" · "Make mobile cleaner"
          </div>
        </div>
      )}

      {/* Section generator inline input */}
      {showSectionInput && (
        <div style={{ padding: '9px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg-2)', flexShrink: 0, display: 'flex', gap: '6px' }}>
          <input
            ref={sectionInputRef}
            value={sectionPrompt}
            onChange={e => setSectionPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddSection(); if (e.key === 'Escape') setShowSectionInput(false) }}
            placeholder="Отзывы клиентов, 3 карточки..."
            style={{ flex: 1, padding: '7px 10px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
          />
          <button
            onClick={handleAddSection}
            disabled={!sectionPrompt.trim() || loading}
            style={{ padding: '7px 13px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, opacity: !sectionPrompt.trim() ? 0.5 : 1, flexShrink: 0 }}
          >↵</button>
        </div>
      )}

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg-2)', flexShrink: 0 }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>✨ AI рекомендует добавить</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {suggestions.map(s => (
              <button key={s.type} onClick={() => addSuggestedBlock(s.type)} disabled={loading}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '5px 8px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-primary)' }}>{s.name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{s.reason}</div>
                </div>
                <span style={{ fontSize: '12px', color: 'var(--accent)', flexShrink: 0 }}>+ добавить</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', gap: '5px' }}>
            {/* Score card */}
            {m.scoreResult && (
              <div style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '14px 14px 14px 4px', border: '1px solid var(--border)' }}>
                <WebsiteScorePanel result={m.scoreResult} compact />
              </div>
            )}
            {/* QA card */}
            {m.qaResult && (
              <div style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '14px 14px 14px 4px', border: '1px solid var(--border)' }}>
                <QaReportPanel result={m.qaResult} />
              </div>
            )}
            {/* Doctor: inline summary + button to open full modal */}
            {m.doctorResult && (
              <div style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '14px 14px 14px 4px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-primary)' }}>{m.content}</div>
                <button
                  onClick={() => setDoctorModal(m.doctorResult!)}
                  style={{ width: '100%', padding: '8px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  🩺 Открыть полный отчёт
                </button>
              </div>
            )}
            {/* Analysis card replaces the text bubble */}
            {!m.scoreResult && !m.qaResult && !m.doctorResult && m.analysis ? (
              <div style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '14px 14px 14px 4px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  📊 Анализ страницы
                </div>
                <SiteAnalysisView a={m.analysis} />
              </div>
            ) : !m.scoreResult && !m.qaResult && !m.doctorResult ? (
            <div style={{
              maxWidth: '86%', padding: '9px 13px',
              borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: m.role === 'user' ? '#2563eb' : 'var(--bg-2)',
              color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
              fontSize: '13px', lineHeight: '1.55', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{m.content}</div>
            ) : null}

            {/* Action confirmation badge */}
            {m.actionDone && (
              <div style={{ padding: '4px 10px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', fontSize: '11px', color: '#15803d', fontWeight: 600 }}>
                {m.actionDone}
              </div>
            )}

            {/* Copy text button */}
            {m.copyText && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigator.clipboard.writeText(m.copyText!).catch(() => null)}
                  style={{ padding: '5px 11px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'inherit' }}
                >📋 Скопировать</button>
                {m.blockApply && (
                  <button
                    onClick={() => {
                      const st = useEditorStore.getState()
                      const blk = st.page.sections.find(s => s.id === m.blockApply!.sectionId)?.blocks.find(b => b.id === m.blockApply!.blockId)
                      if (blk) st.updateBlock(m.blockApply!.sectionId, m.blockApply!.blockId, { ...blk.data, ...m.blockApply!.data })
                    }}
                    style={{ padding: '5px 11px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 600 }}
                  >↵ Применить в блок</button>
                )}
              </div>
            )}

            {/* Theme color swatches */}
            {m.themeColors && Object.keys(m.themeColors).length > 0 && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '8px 10px', background: 'var(--bg-2)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                {Object.entries(m.themeColors).map(([label, hex]) => (
                  <div key={label} title={`${label}: ${hex}`} onClick={() => navigator.clipboard.writeText(hex).catch(() => null)}
                    style={{ width: '28px', height: '28px', borderRadius: '8px', background: hex, border: '2px solid rgba(0,0,0,0.12)', cursor: 'pointer', flexShrink: 0 }} />
                ))}
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>↑ нажмите для копирования</span>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '9px 14px', borderRadius: '14px 14px 14px 4px', background: 'var(--bg-2)', fontSize: '18px', letterSpacing: '3px', opacity: 0.6 }}>▪▪▪</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* §74 Pending transaction — visual diff view */}
      {pendingTx && (
        <div style={{ borderTop: '2px solid var(--accent)', background: 'var(--bg-1)', flexShrink: 0 }}>
          <div style={{ padding: '8px 12px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14 }}>✨</span>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                AI предлагает {pendingTx.actions.length} изменен{pendingTx.actions.length === 1 ? 'ие' : 'ий'}
              </span>
            </div>
          </div>
          <div style={{ maxHeight: '140px', overflowY: 'auto', padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pendingTx.actions.map((a, i) => {
              const destructive = isDestructive(a)
              const icon = a.type === 'add_section' ? '➕' : a.type === 'remove_block' ? '🗑️' : a.type === 'update_block' ? '✏️' : '🎨'
              const bg = destructive ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.07)'
              const border = destructive ? 'rgba(245,158,11,0.3)' : 'rgba(34,197,94,0.25)'
              const color = destructive ? '#92400e' : '#166534'
              let detail = ''
              if (a.type === 'add_section') {
                detail = `${a.blocks.length} блок${a.blocks.length > 1 ? 'а' : ''}: ${a.blocks.slice(0, 3).map((b: { type: string }) => b.type).join(', ')}${a.blocks.length > 3 ? '...' : ''}`
              } else if (a.type === 'update_block') {
                const keys = Object.keys(a.data ?? {}).slice(0, 4)
                detail = `поля: ${keys.join(', ')}`
              } else if (a.type === 'change_theme') {
                detail = `accent: ${a.accentColor ?? '—'}`
              }
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '5px 8px', background: bg, border: `1px solid ${border}`, borderRadius: 7 }}>
                  <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color, lineHeight: 1.3 }}>{actionLabel(a)}</div>
                    {detail && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</div>}
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: '6px', padding: '8px 12px 10px' }}>
            <button
              onClick={() => {
                let actionDone = ''
                for (const action of pendingTx.actions) {
                  const done = applyAction(action)
                  if (done) actionDone = done
                }
                setMessages(prev => {
                  const copy = [...prev]
                  const last = copy[copy.length - 1]
                  if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, actionDone: actionDone || '✅ Применено' }
                  return copy
                })
                setPendingTx(null)
              }}
              style={{ flex: 1, padding: '8px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
            >✓ Применить всё</button>
            <button
              onClick={() => {
                setMessages(prev => {
                  const copy = [...prev]
                  const last = copy[copy.length - 1]
                  if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, actionDone: '✕ Отклонено' }
                  return copy
                })
                setPendingTx(null)
              }}
              style={{ flex: 1, padding: '8px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}
            >✕ Отклонить</button>
          </div>
        </div>
      )}

      {/* Text input */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '7px', flexShrink: 0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input) } }}
          placeholder="Спросите что-нибудь..."
          disabled={loading}
          style={{ flex: 1, padding: '9px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '13px', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
        />
        <button
          onClick={() => void send(input)}
          disabled={loading || !input.trim()}
          style={{ padding: '9px 13px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, opacity: loading || !input.trim() ? 0.45 : 1, flexShrink: 0 }}
        >↑</button>
      </div>
    </div>

    {/* Website Doctor full modal */}
    {doctorModal && <WebsiteDoctorModal result={doctorModal} onClose={() => setDoctorModal(null)} />}
    </>
  )
}
